import { authService } from "../../features/auth/services/auth.service";
import { API_CONFIG } from "../apiConfig";
import { User } from "../../features/auth/interfaces/auth.interface";
import { apiService } from "../api/api.service";

// Token management utilities
export class TokenManager {
  private static isRefreshing = false;
  private static refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private static failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: any) => void;
  }> = [];
  private static isValidating = false;
  private static validationPromise: Promise<void> | null = null;

  private static processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token!);
      }
    });
    this.failedQueue = [];
  }

  // Store tokens securely
  static setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(API_CONFIG.TOKEN_STORAGE_KEY, accessToken);
    localStorage.setItem(API_CONFIG.REFRESH_TOKEN_KEY, refreshToken);

    // schedule automatic token refresh
    this.scheduleTokenRefresh();
  }

  // Validate current tokens and refresh if needed (call on app startup)
  static async validateAndRefreshIfNeeded(): Promise<void> {
    // If validation is already in progress, return the existing promise
    if (this.isValidating && this.validationPromise) {
      return this.validationPromise;
    }

    this.isValidating = true;
    this.validationPromise = this.doValidation();

    try {
      await this.validationPromise;
    } catch (error) {
      console.error('Token validation failed:', error);
      // If validation fails, clear corrupted state
      await this.clearTokens();
      throw error;
    } finally {
      this.isValidating = false;
      this.validationPromise = null;
    }
  }

  private static async doValidation(): Promise<void> {
    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    if (!token || !refreshToken) {
      return;
    }

    // Check if token is corrupted
    if (this.isTokenCorrupted(token)) {
      console.warn('Token appears corrupted, clearing storage');
      await this.clearTokens();
      return;
    }

    // Use the same API service to validate token for consistency
    try {
      const response = await apiService.get('/auth/me');
      this.scheduleTokenRefresh();
    } catch (error: any) {
      if (error.statusCode === 401) {
        try {
          await this.refreshToken();
          this.scheduleTokenRefresh();
        } catch (refreshError) {
          console.error('Token refresh failed during validation:', refreshError);
          await this.clearTokens();
        }
      } else {
        // Try refresh anyway for safety
        try {
          await this.refreshToken();
          this.scheduleTokenRefresh();
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          await this.clearTokens();
        }
      }
    }
  }

  // Check if validation is in progress (for components to wait)
  static get isValidatingTokens(): boolean {
    return this.isValidating;
  }

  // Get access token
  static getAccessToken(): string | null {
    return localStorage.getItem(API_CONFIG.TOKEN_STORAGE_KEY);
  }

  // Get refresh token
  static getRefreshToken(): string | null {
    return localStorage.getItem(API_CONFIG.REFRESH_TOKEN_KEY);
  }

  // Clear all tokens and sensitive storage. Call this on explicit user logout.
  static async clearTokens(): Promise<void> {
    this.cancelScheduledRefresh();
    await this.deepClearStorage();
  }

  // Aggressively clear all local browser data except cookie preferences
  private static async deepClearStorage(): Promise<void> {
    try {
      // 1. Clear Standard Web Storage, preserving cookie data
      const preserveKeys = ['cookiesModalSeen', 'cookiesAccepted', 'cookiePreferences'];
      
      // LocalStorage
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && !preserveKeys.includes(key)) {
          localStorage.removeItem(key);
        }
      }
      
      // SessionStorage - safe to clear entirely
      sessionStorage.clear();

      // 2. Clear all Cookies, except those related to cookie preferences
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        
        // Preserve cookies that contain 'cookie' in their name (e.g., cookieConsent)
        if (name && !name.toLowerCase().includes('cookie')) {
          document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
        }
      }

      // 3. Clear IndexedDB
      if (window.indexedDB && window.indexedDB.databases) {
        const dbs = await window.indexedDB.databases();
        for (const db of dbs) {
          if (db.name) {
            window.indexedDB.deleteDatabase(db.name);
          }
        }
      } else {
        // Fallback for browsers not supporting databases() metadata
        const possibleDBNames = ['baoafrik-db', 'firebase-messaging-sw'];
        possibleDBNames.forEach(name => window.indexedDB.deleteDatabase(name));
      }

      // 4. Force reload to clear remaining transient memory state
      // (Optional, but often best for security)
      // window.location.href = '/login'; 

    } catch (error) {
      console.error("Storage clearing failed partially:", error);
    }
  }

  // Cancel any scheduled token refresh (e.g. on logout)
  static cancelScheduledRefresh(): void {
    if (this.refreshTimeoutId !== null) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }

  // Get user from storage
  static getUser(): User | null {
    try {
      const userStr = localStorage.getItem(API_CONFIG.USER_STORAGE_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      return null;
    }
  }

  // Set user to storage
  static setUser(user: User): void {
    localStorage.setItem(API_CONFIG.USER_STORAGE_KEY, JSON.stringify(user));
  }

  // Set visitor mode
  static setVisitor(isVisitor: boolean): void {
    if (isVisitor) {
      localStorage.setItem(API_CONFIG.VISITOR_STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(API_CONFIG.VISITOR_STORAGE_KEY);
    }
  }

  // Check if in visitor mode
  static isVisitor(): boolean {
    return localStorage.getItem(API_CONFIG.VISITOR_STORAGE_KEY) === "true";
  }

  // Check if user is authenticated
  static isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return token !== null && !this.isTokenExpired(token) && !this.isTokenCorrupted(token);
  }

  // Check if token is corrupted
  static isTokenCorrupted(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;

      // Try to parse payload
      const payload = JSON.parse(atob(parts[1]));
      if (!payload.exp || !payload.iat) return true;

      // Check for reasonable expiration time
      const now = Date.now() / 1000;
      const exp = payload.exp;
      const iat = payload.iat;

      // Token should not be expired by more than 1 day (indicates corruption)
      if (exp < now - 86400) return true;

      // Token should not be issued in the future
      if (iat > now + 300) return true; // 5 minute tolerance

      return false;
    } catch (error) {
      return true; // If we can't parse the token, it's corrupted
    }
  }

  // Check if token is expired
  static isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true; // If we can't parse the token, consider it expired
    }
  }

  // Get token expiration time
  static getTokenExpiration(token: string): number | null {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      return null;
    }
  }

  // Refresh token method
  static async refreshToken(): Promise<string> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      const currentRefreshToken = this.getRefreshToken();
      if (!currentRefreshToken) throw new Error("No refresh token available");

      const response = await authService.refreshToken(currentRefreshToken);

      if (!response.success || !response.data) {
        throw new Error(response.message || "Token refresh failed");
      }

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Validate new tokens before storing
      if (this.isTokenCorrupted(accessToken)) {
        throw new Error('Received corrupted access token');
      }

      const previousAccessToken = this.getAccessToken();
      localStorage.setItem(API_CONFIG.TOKEN_STORAGE_KEY, accessToken);
      localStorage.setItem(API_CONFIG.REFRESH_TOKEN_KEY, newRefreshToken);

      // Notify other tabs of token update. `oldValue` must be captured before
      // the write so listeners can distinguish a real change from a no-op.
      window.dispatchEvent(new StorageEvent('storage', {
        key: API_CONFIG.TOKEN_STORAGE_KEY,
        oldValue: previousAccessToken,
        newValue: accessToken,
      }));

      this.processQueue(null, accessToken);
      return accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.processQueue(error, null);
      // Clear tokens on refresh failure to prevent stuck state
      await this.clearTokens();
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  // Auto-refresh token before expiration. Keeps user logged in; only logout clears tokens.
  static scheduleTokenRefresh(): void {
    this.cancelScheduledRefresh();

    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    if (!token || !refreshToken) {
      return;
    }

    const expirationTime = this.getTokenExpiration(token);
    if (!expirationTime) {
      return;
    }

    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    const refreshBeforeMs = 2 * 60 * 1000; // Refresh 2 minutes before expiry (more conservative)
    const refreshTime = timeUntilExpiry - refreshBeforeMs;

    const scheduleNext = () => {
      this.scheduleTokenRefresh();
    };

    const onRefreshError = () => {
      // Do not clear tokens; retry after a short delay (e.g. network blip)
      const retryDelayMs = 60 * 1000; // 1 minute
      this.refreshTimeoutId = setTimeout(() => {
        this.refreshTimeoutId = null;
        this.scheduleTokenRefresh();
      }, retryDelayMs);
    };

    if (refreshTime > 0) {
      this.refreshTimeoutId = setTimeout(async () => {
        this.refreshTimeoutId = null;
        try {
          await this.refreshToken();
          scheduleNext();
        } catch {
          onRefreshError();
        }
      }, refreshTime);
    } else if (timeUntilExpiry > 30000) { // Only refresh immediately if more than 30 seconds left
      // Token expires soon but still has time; refresh immediately
      this.refreshToken().then(scheduleNext).catch(onRefreshError);
    } else {
      // Token is very close to expiry or expired; don't auto-refresh to avoid loops
    }
  }

  // get time until token expiry
  static getTimeUntilExpiry(): number {
    const token = this.getAccessToken();
    if (!token) return 0;

    const expirationTime = this.getTokenExpiration(token);
    if (!expirationTime) return 0;

    return (expirationTime - Date.now()) / (60 * 1000);
  }
}
